/** @jsx isolateComponent */
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { css } from '@emotion/react';

import { Icon } from '../../display/Icon';
import { Tooltip } from '../../display/Tooltip';
import { TextInput } from '../../forms/TextInput';
import type { TextInputProps } from '../../forms/TextInput';
import { Animation, Colors } from '../../../theme';
import { useCopyToClipboard } from '../../../hooks';

const Styles = labelStyles('CopyTextInput', {
  root: css`
    display: block;
  `,
  copyButton: css`
    position: relative;
    color: ${Colors.neutral600};

    &:hover,
    &:focus {
      color: ${Colors.blue};
    }

    &[disabled] {
      color: ${Colors.neutral500};
      cursor: not-allowed;
    }
  `,
  tooltip: css`
    opacity: 1;
    transition: opacity ${Animation.transitionDuration}
      ${Animation.transitionTimingFunction};
    &[data-hidden='true'] {
      opacity: 0;
    }
  `,
});

type CopyTextInputProps = Omit<
  TextInputProps,
  'isMultiline' | 'isFullWidth' | 'renderIconAfter'
> & {
  /** Same value as TextInputProps and it is required */
  value: string;
};

export const CopyTextInput = (props: CopyTextInputProps) => {
  const [didCopy, handleCopyText] = useCopyToClipboard(props.value);
  const onClickCopy = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    handleCopyText();
  };
  const value = props.value || '';
  const renderCopyButton = ({ iconSize }: { iconSize: number }) => {
    const ariaLabel = didCopy
      ? `"${value}" copied to the clipboard`
      : `Copy "${value}" to the clipboard`;
    return (
      <button
        aria-hidden={!value}
        aria-label={ariaLabel}
        color="primary"
        css={Styles.copyButton}
        disabled={!value}
        onClick={onClickCopy}
        type="button"
      >
        <Icon isBlock={true} name="actionCopy" size={iconSize} />
        <div css={Styles.tooltip} data-hidden={!didCopy}>
          <Tooltip ariaHidden={true} position="southeast">
            Copied to clipboard!
          </Tooltip>
        </div>
      </button>
    );
  };
  return (
    <div css={Styles.root}>
      <TextInput
        {...props}
        ariaHidden={true}
        disabledText={props.value ? '' : 'Disabled'}
        isDisabled={!props.value}
        isReadonly={Boolean(props.value)}
        isFocusable={false}
        renderIconAfter={renderCopyButton}
      />
    </div>
  );
};

CopyTextInput.defaultProps = {
  type: 'text',
};
